import React, { PureComponent } from 'react';
import { NavLink } from 'react-router-dom';
import { TableRowData, TableColumn, TableColumn$RenderProps, RowActionsRender } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';

// utils
import truncateTextWithTooltip from 'utils/truncateTextWithTooltip';
import { convertArrayToCamelCase } from 'utils/helpers';
import { formatDate } from 'utils/formatDate';

// types
import ItemsState from 'ducks/merchantDucks/items/types';

// images
import defaultPreviewImg from 'assets/pics/ip-preview-premium.jpg';

// componenets
import GenericTable from 'components/Table/GenericTable';
import CommonIcon from 'components/CommonIcon';
import TableClipBoardIcon from 'components/Table/TableClipBoardIcon';

const TableImage = styled.img`
  width: 50px;
  border-radius: 3px;
`;

export interface Asset {
  id: number;
  title: string;
  updated_at: number;
  created_at: number;
  item_type: { name: string; description: string };
  metadata: { thumbnail_image: string; paywall_cover_photo: string };
  is_active: boolean;
}

export interface AssetTableData extends TableRowData {
  title: string;
  updatedAt: number;
  createdAt: number;
  itemType: {
    name: string;
    description: string;
  };
  metadata: {
    thumbnail_image: string;
    paywall_cover_photo: string;
  };
  isActive: boolean;
}

interface Props {
  handleReactivateAsset: (id: number) => any;
  handlePageClick: (page: number) => any;
  assets: ItemsState;
  paginationPage: number;
  renderHeaderSection: () => Node | JSX.Element;
}

export default class AssetsTable extends PureComponent<Props> {
  public render() {
    const {
      assets: { collection, total, limit, isFetching },
      handlePageClick,
      paginationPage,
      renderHeaderSection,
    } = this.props;

    const assetsCollection = convertArrayToCamelCase<Asset, AssetTableData>(collection);

    return (
      <GenericTable<AssetTableData>
        hasBottomPagination
        columns={this.generateColumns()}
        data={assetsCollection}
        total={total}
        itemsPerPage={limit}
        currentPage={paginationPage}
        handlePageClick={handlePageClick}
        showLoader={isFetching}
        pageRangeDisplayed={3}
        options={{
          rowActions: this.renderRowActions,
          headerSection: renderHeaderSection(),
        }}
        type="assets"
        actionsRowTitle="Edit"
      />
    );
  }

  private generateColumns = (): Array<TableColumn<AssetTableData>> => [
    {
      title: '',
      key: '',
      render: ({ rowValues }) => {
        const {
          itemType: { name: assetType },
          metadata,
        } = rowValues;

        if (assetType === 'qbrick_asset' || assetType === 'brightcove_asset') {
          return <TableImage alt="thumbnail-preview-img" src={metadata?.paywall_cover_photo || defaultPreviewImg} />;
        }

        return <TableImage alt="thumbnail-preview-img" src={metadata?.thumbnail_image || defaultPreviewImg} />;
      },
    },
    {
      title: 'Name',
      key: 'title',
      render: ({ rowValues: { id }, value }) => (
        <TableClipBoardIcon tag={`icon_copy_asset_${id}`} clipboardText={value}>
          {truncateTextWithTooltip({ text: value })}
        </TableClipBoardIcon>
      ),
    },
    {
      title: 'ID',
      key: 'id',
    },
    {
      title: 'Type',
      key: 'itemType',
      render: ({ value }) => value.description,
    },
    {
      title: 'Created at (UTC)',
      key: 'createdAt',
      render: ({ value }: TableColumn$RenderProps<AssetTableData, number>) => formatDate(value, 'YYYY/DD/MM HH:mm'),
    },
    {
      title: 'Updated at (UTC)',
      key: 'updatedAt',
      render: ({ value }: TableColumn$RenderProps<AssetTableData, number>) => formatDate(value, 'YYYY/DD/MM HH:mm'),
    },
  ];

  private renderRowActions: RowActionsRender<AssetTableData> = ({ row }) => {
    const { handleReactivateAsset } = this.props;

    return row.isActive ? (
      <NavLink to={`/assets/edit/${row.id}`}>
        <CommonIcon tag={`icon_edit_${row.id}`} hoverStatus="edit" name="edit" />
      </NavLink>
    ) : (
      <a
        href="#/"
        onClick={(e) => {
          e.preventDefault();
          handleReactivateAsset(Number(row.id));
        }}
      >
        Reactivate asset
      </a>
    );
  };
}
